/**
 * BlogCard Component
 * Displays a blog item with status and actions
 */

import { Calendar, Eye, Edit, Trash2, Copy, FileImage, Search, ExternalLink } from 'lucide-react';
import type { BlogWithRelations, BlogStatus } from '@/types/blogger';

interface BlogCardProps {
  blog: BlogWithRelations;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}

const STATUS_STYLES: Record<BlogStatus, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
  published: { bg: 'bg-green-100', text: 'text-green-700', label: 'Published' },
  archived: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Archived' },
};

export function BlogCard({
  blog,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
}: BlogCardProps) {
  const statusStyle = STATUS_STYLES[blog.status];
  const formattedDate = new Date(blog.created_at).toLocaleDateString();
  const shopifyUrl = blog.shopify_article_id
    ? `https://mcgrocer-com.myshopify.com/admin/articles/${blog.shopify_article_id}`
    : null;

  const handleCardClick = () => {
    if (shopifyUrl) {
      window.open(shopifyUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      {/* Featured Image - clickable to Shopify if published */}
      <div
        className={`w-full h-48 overflow-hidden bg-gray-100 relative ${shopifyUrl ? 'cursor-pointer group' : ''}`}
        onClick={handleCardClick}
      >
        {blog.featured_image_url ? (
          <img
            src={blog.featured_image_url}
            alt={blog.featured_image_alt || blog.title}
            className={`w-full h-full object-cover ${shopifyUrl ? 'group-hover:opacity-90 transition-opacity' : ''}`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <FileImage className="w-16 h-16 text-gray-400" />
          </div>
        )}
        {/* Shopify indicator overlay */}
        {shopifyUrl && (
          <div className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
            <ExternalLink className="w-4 h-4 text-purple-600" />
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <h3
            className={`font-semibold text-gray-900 text-lg line-clamp-2 flex-1 ${shopifyUrl ? 'cursor-pointer hover:text-purple-600 transition-colors' : ''}`}
            onClick={handleCardClick}
            title={shopifyUrl ? 'Open in Shopify' : undefined}
          >
            {blog.title}
          </h3>
          <span
            className={`px-2 py-1 text-xs font-medium rounded ${statusStyle.bg} ${statusStyle.text}`}
          >
            {statusStyle.label}
          </span>
        </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{formattedDate}</span>
          </div>
          {blog.word_count && (
            <span>{blog.word_count.toLocaleString()} words</span>
          )}
        </div>

        {blog.persona && (
          <p className="text-sm text-gray-600">
            <span className="font-medium">Persona:</span> {blog.persona.name}
          </p>
        )}

        {blog.template && (
          <p className="text-sm text-gray-600">
            <span className="font-medium">Template:</span> {blog.template.name}
          </p>
        )}
      </div>

        <div className="flex gap-2 pt-3 border-t border-gray-200">
          <button
            onClick={() => onView(blog.id)}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100
              rounded-md hover:bg-gray-200 flex items-center justify-center gap-2"
          >
            <Eye className="w-4 h-4" />
            View
          </button>
          <button
            onClick={() => onEdit(blog.id)}
            className="flex-1 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100
              rounded-md hover:bg-blue-200 flex items-center justify-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => onDuplicate(blog.id)}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100
              rounded-md hover:bg-gray-200"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <a
            href={`https://fxkjblrlogjumybceozk.supabase.co/functions/v1/blog-preview/${blog.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-100
              rounded-md hover:bg-indigo-200"
            title="SEO Preview"
            onClick={(e) => e.stopPropagation()}
          >
            <Search className="w-4 h-4" />
          </a>
          <button
            onClick={() => onDelete(blog.id)}
            className="px-3 py-2 text-sm font-medium text-red-700 bg-red-100
              rounded-md hover:bg-red-200"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
